import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import type { PetitionSignatureCountState } from '#src/app/pages/petitionDetails/shared/types';

import { usePetitionSignatureCount } from '../petitionStats/usePetitionSignatureCount';

type SignFormSignatureCountExperiment = PetitionDetailsPageUserData['experiments']['signFormSignatureCountExperiment'];

type SignatureCountExperimentResult = Readonly<{
	experiment: SignFormSignatureCountExperiment;
	/**
	 * 'count' — aggregate count, petition at/above the floor; 'early_signer' — below the floor
	 * (or count unavailable/0), the prompt shows the threshold, never the live count;
	 * 'none' — control arms.
	 */
	displayMode: 'count' | 'early_signer' | 'none';
	suppressTicker: boolean;
	/** The payload signatureFloor — the number the early-signer prompt displays. */
	threshold: number;
	signatureCount: PetitionSignatureCountState['signatureCount'];
}>;

export function useSignatureCountExperiment(): SignatureCountExperimentResult {
	const {
		data: { experiments },
	} = usePetitionDetailsPageUserData();
	const { signatureCount } = usePetitionSignatureCount();

	const experiment = experiments.signFormSignatureCountExperiment;

	// Fallback resolutions always carry variation 'control' (growthbookControl()), so a fallback
	// can never reach a treated mode — no fallback/override check needed here.
	const treated = experiment.variation === 'variant_1' || experiment.variation === 'variant_2';
	const atOrAboveFloor = signatureCount.displayed > 0 && signatureCount.displayed >= experiment.config.signatureFloor;

	let displayMode: SignatureCountExperimentResult['displayMode'] = 'none';
	if (treated) {
		displayMode = atOrAboveFloor ? 'count' : 'early_signer';
	}

	// variant_1 suppresses the ticker on BOTH sides of the floor (BEHAVIOR 3 + amended BEHAVIOR 5)
	const suppressTicker = experiment.variation === 'variant_1';

	return { experiment, displayMode, suppressTicker, threshold: experiment.config.signatureFloor, signatureCount };
}
